 /**
  * SEO HEADING STRUCTURE - 함께 공부하기 (Korean)
  */
 
 import { Header } from "@/components/Header";
 import { Footer } from "@/components/Footer";
 import { SEO } from "@/components/SEO";
 import { Button } from "@/components/ui/button";
 import { Card, CardContent } from "@/components/ui/card";
 import { Users, Clock, Heart, Globe, ArrowRight, BookOpen, Target, MessageCircle, Sparkles } from "lucide-react";
 import { Link } from "react-router-dom";
 
 const features = [
   {
     icon: Users,
     title: "글로벌 스터디 커뮤니티",
     description: "전 세계 학생들과 함께 실시간으로 공부하세요. 시간대에 상관없이 항상 함께할 사람이 있습니다.",
   },
   {
     icon: Clock,
     title: "24/7 지원",
     description: "언제든지 접속하세요. 새벽 3시든 오후 3시든, 누군가 항상 공부하고 있습니다.",
   },
   {
     icon: Heart,
     title: "동기부여와 책임감",
     description: "다른 사람들이 공부하는 모습을 보며 동기부여를 받고, 함께하는 책임감을 느끼세요.",
   },
   {
     icon: Globe,
     title: "다양한 사람들",
     description: "다양한 배경과 목표를 가진 사람들과 교류하며 새로운 시각을 얻으세요.",
   },
   {
     icon: Target,
     title: "목표 달성",
     description: "개인 목표를 설정하고 커뮤니티와 함께 달성해 나가세요.",
   },
   {
     icon: MessageCircle,
     title: "소통과 지원",
     description: "어려울 때 도움을 요청하고, 성공을 함께 축하하세요.",
   },
 ];
 
 export default function StudyWithMeKr() {
   return (
     <div className="min-h-screen bg-background">
       <SEO 
         title="함께 공부하기 | CSW | Online Workspace | Productivity Tool | Study Stream"
         description="전 세계 학생들과 함께 실시간으로 공부하세요. 24/7 글로벌 스터디 커뮤니티에서 동기부여와 책임감을 얻으세요."
         keywords="함께 공부하기, 스터디 윗 미, 온라인 스터디, 공부 커뮤니티, 바디 더블링, 스터디 그룹"
         canonicalUrl="/kr/study-with-me/"
       />
       <Header />
       
       <main>
         {/* Hero Section */}
         <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
           <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
           <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
           
           <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
             <div className="max-w-4xl mx-auto text-center space-y-8 animate-fade-in">
               <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-card/50 border border-border backdrop-blur-sm">
                 <div className="w-2 h-2 bg-primary rounded-full animate-glow-pulse" />
                 <span className="text-sm font-medium text-muted-foreground">글로벌 커뮤니티</span>
               </div>
               
               <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                 <span className="text-gradient">함께 공부하기</span>
               </h1>
               
               <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl mx-auto">
                 혼자가 아닌 함께. 전 세계 학생들과 실시간으로 공부하세요.
               </p>
               
               <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                 <Button 
                   size="lg" 
                   className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                   asChild
                 >
                   <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                     지금 참여하기
                     <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                   </a>
                 </Button>
               </div>
               
               <div className="flex items-center justify-center gap-8 pt-8">
                 <div>
                   <p className="text-3xl font-bold text-gradient">24/7</p>
                   <p className="text-sm text-muted-foreground">항상 열려있음</p>
                 </div>
                 <div className="w-px h-12 bg-border" />
                 <div>
                   <p className="text-3xl font-bold text-gradient">글로벌</p>
                   <p className="text-sm text-muted-foreground">전 세계 학생들</p>
                 </div>
                 <div className="w-px h-12 bg-border" />
                 <div>
                   <p className="text-3xl font-bold text-gradient">무료</p>
                   <p className="text-sm text-muted-foreground">영원히</p>
                 </div>
               </div>
             </div>
           </div>
         </section>
 
         {/* Features Grid */}
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-muted to-background" />
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
           
           <div className="container mx-auto max-w-6xl relative z-10">
             <div className="text-center mb-16 animate-fade-in">
               <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                 왜 <span className="text-gradient">함께</span> 공부해야 할까요?
               </h2>
               <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                 사회적 촉진 효과와 바디 더블링으로 집중력을 높이세요
               </p>
             </div>
 
             <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
               {features.map((feature) => {
                 const Icon = feature.icon;
                 return (
                   <Card
                     key={feature.title}
                     className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-xl"
                   >
                     <CardContent className="p-8 space-y-4">
                       <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                         <Icon className="w-6 h-6 text-primary" />
                       </div>
                       
                       <h3 className="text-xl font-semibold text-foreground font-display">
                         {feature.title}
                       </h3>
                       
                       <p className="text-muted-foreground leading-relaxed">
                         {feature.description}
                       </p>
                     </CardContent>
                   </Card>
                 );
               })}
             </div>
           </div>
         </section>
 
         {/* CTA Section */}
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-background via-primary/5 to-background" />
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-primary/20 rounded-full blur-[150px]" />
           
           <div className="max-w-4xl mx-auto text-center relative z-10">
             <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
               <span className="text-gradient">커뮤니티</span>에 참여하세요
             </h2>
             <p className="text-xl text-muted-foreground mb-10 max-w-2xl mx-auto">
               전 세계 수천 명의 학생들이 이미 함께 공부하고 있습니다.
             </p>
             <Button 
               size="lg" 
               className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg glow-primary transition-all hover:scale-105"
               asChild
             >
               <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                 무료로 시작하기
                 <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
               </a>
             </Button>
           </div>
         </section>
 
         {/* Related Pages */}
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
           
           <div className="container mx-auto max-w-6xl relative z-10">
             <div className="text-center mb-12">
               <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                 더 많은 <span className="text-gradient">기능</span> 살펴보기
               </h2>
             </div>
             
             <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
               {[
                 { name: "스터디 스트림", path: "/kr/study-stream/" },
                 { name: "온라인 작업 공간", path: "/kr/online-workspace/" },
                 { name: "스터디 타이머", path: "/kr/study-timer/" },
                 { name: "친구와 공부하기", path: "/kr/study-with-friends/" },
               ].map((link) => (
                 <Link
                   key={link.path}
                   to={link.path}
                   className="group p-4 rounded-xl bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/50 transition-all hover:scale-105 hover:shadow-lg text-center"
                 >
                   <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                     {link.name}
                   </span>
                 </Link>
               ))}
             </div>
           </div>
         </section>
       </main>
       
       <Footer />
     </div>
   );
 }